// React
import { useEffect, useRef, useState } from "react";

// OpenLayers
import OlMap from "ol/Map";
import { ScaleLine as OlScaleLine } from "ol/control";
import { Units } from "ol/control/ScaleLine";

// UI
import ScaleUnitPopup from "views/popups/ScaleUnitPopup";

// Tools
import { getOffsetBottomPixels } from "lib/utilities/misc";

// jQuery
import $ from "jquery";

import "styles/css/ScaleLine.scss";

interface ScaleBarProps {
	olMap: OlMap;
}

let scaleLine: OlScaleLine;

export default function ScaleLine({ olMap }: ScaleBarProps) {
	const dragRef = useRef<HTMLDivElement>(null);
	const [isDragging, setDragging] = useState(false);
	const [customPosition, setCustomPosition] = useState(false);
	const movedRef = useRef(false);
	const origPosRef = useRef<{ left: number; bottom: number }>({ left: 0, bottom: 0 });

	useEffect(() => {
		const defaultUnits: string = _config.defaultScaleLineUnits;
		if (_config.scaleUnitOptions.indexOf(defaultUnits) < 0)
			throw new Error(`Invalid scale bar units '${defaultUnits}'`);

		const unit: Units = defaultUnits as Units;

		scaleLine = new OlScaleLine({
			units: unit,
			maxWidth: 90,
			target: "scale-line",
		});

		olMap.getControls().extend([scaleLine]);
		olMap.set("scale-line", scaleLine);
		_ui.setScaleLineUnits(scaleLine.getUnits(), true);

		setTimeout(adjust, 100);
		setTimeout(adjust, 250);
		setTimeout(adjust, 500);
	}, [olMap]);

	useEffect(() => {
		if (!isDragging && !customPosition) adjust();
	}, [_dimensions, isDragging, customPosition]);

	const adjust = () => {
		if (customPosition) return;

		const width = _dimensions.width;
		if (width < 340) {
			$("#scale-line").fadeOut(_ui.transitionDownOptions);
		} else {
			$("#scale-line").fadeIn(_ui.transitionUpOptions);
		}

		const paddingLeft =
			($("#base-map-switcher").position()?.left || 40) +
			(width > 400 ? 15 : 10);
		const left = ($("#base-map-switcher").width() || 0) + paddingLeft;
		$("#scale-line").css("left", left + "px");

		const bmBottom = getOffsetBottomPixels("#base-map-switcher");
		if (bmBottom) {
			$("#scale-line").css("bottom", bmBottom - 5 + "px");
		}
	};

	const onScaleLineClicked = () => {
		const scaleUnitOptions: string[] = _config.scaleUnitOptions;
		ScaleUnitPopup.set("map", olMap);
		ScaleUnitPopup.set("unit-options", scaleUnitOptions);
		_ui.showPopup(ScaleUnitPopup);
	};

	useEffect(() => {
		const el = dragRef.current;
		if (!el) return;

		let startX = 0;
		let startY = 0;

		const getClient = (e: MouseEvent | TouchEvent) => {
			if ('touches' in e) {
				return { x: e.touches[0].clientX, y: e.touches[0].clientY };
			}
			return { x: (e as MouseEvent).clientX, y: (e as MouseEvent).clientY };
		};

		const onPointerDown = (e: MouseEvent | TouchEvent) => {
			setDragging(true);
			movedRef.current = false;

			const { x, y } = getClient(e);
			startX = x;
			startY = y;

			const rect = el.getBoundingClientRect();
			origPosRef.current = { left: rect.left, bottom: window.innerHeight - rect.bottom };

			document.addEventListener('mousemove', onPointerMove);
			document.addEventListener('touchmove', onPointerMove, { passive: false });
			document.addEventListener('mouseup', onPointerUp);
			document.addEventListener('touchend', onPointerUp);
		};

		const onPointerMove = (e: MouseEvent | TouchEvent) => {
			e.preventDefault();

			const { x, y } = getClient(e);
			const dx = x - startX;
			const dy = y - startY;

			// threshold to mark movement
			if (!movedRef.current && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
				movedRef.current = true;
			}

			el.style.left = origPosRef.current.left + dx + "px";
			el.style.bottom = origPosRef.current.bottom - dy + "px";
		};

		const onPointerUp = () => {
			
			if (movedRef.current) {
				const left = parseFloat(el.style.left) || 0;
				const bottom = parseFloat(el.style.bottom) || 0;
				origPosRef.current = { left, bottom };
				movedRef.current = false;
				setCustomPosition(true);
			}else{
				onScaleLineClicked();
			}
			setDragging(false);

			document.removeEventListener('mousemove', onPointerMove);
			document.removeEventListener('touchmove', onPointerMove);
			document.removeEventListener('mouseup', onPointerUp);
			document.removeEventListener('touchend', onPointerUp);
		};

		el.addEventListener('mousedown', onPointerDown);
		el.addEventListener('touchstart', onPointerDown, { passive: false });
		return () => {
			el.removeEventListener('mousedown', onPointerDown);
		
el.removeEventListener('touchstart', onPointerDown);
		};
	}, []);

	return (
		<div
			id="scale-line"
			ref={dragRef}
			className="scale-line"
			style={{ position: "absolute", cursor: "move", touchAction: "none" }}
		></div>
	);
}